import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  integrationCalls,
  type IntegrationCall,
  type IntegrationCallFilter,
  type IntegrationCallStatus,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { nomeDoProvedor } from '@/shared/lib/nomeDoProvedor';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';

import { CallDetailModal, CallStatusBadge, ResendDialog } from './IntegrationCallParts';
import { opcoesDaSituacao } from './rotulosDoRegistro';

/**
 * Consultas a bureau: o "Gerenciamento de consultas a bureau" do OnPoint
 * (/gerenciamento-consultas-bureau).
 *
 * Lista as consultas que a empresa fez aos bureaus, com Data/Hora, Id da consulta,
 * CNPJ, Bureau usado e Mensagem. Quem tem a caixa "Habilitar filtro mandatorio" no
 * perfil ve o aviso "Atencao!" e so lista depois de informar o periodo ou o CNPJ; o
 * servidor confere a mesma coisa.
 */

const LIMITE = 25;

interface Filtros {
  from: string;
  to: string;
  document: string;
  provider_key: string;
  status: string;
}

const filtrosVazios: Filtros = {
  from: '',
  to: '',
  document: '',
  provider_key: '',
  status: '',
};

function digitos(texto: string): string {
  return texto.replace(/\D/g, '');
}

/** O mesmo recorte que o servidor aceita para o filtro mandatorio. */
function recorteSuficiente(filtros: Filtros): boolean {
  const documento = digitos(filtros.document);
  return (
    (filtros.from !== '' && filtros.to !== '') ||
    documento.length === 11 ||
    documento.length === 14
  );
}

export function BureauConsultationsPage() {
  const { can } = useAuth();
  const filtroMandatorio = can('integracoes.filtro_mandatorio.update');
  const podeReenviar = can('integracoes.integracao.update');

  const [rascunho, setRascunho] = useState<Filtros>(filtrosVazios);
  const [aplicados, setAplicados] = useState<Filtros>(filtrosVazios);
  const [avisoAberto, setAvisoAberto] = useState(filtroMandatorio);
  const [offset, setOffset] = useState(0);
  const [detalhe, setDetalhe] = useState<string | null>(null);
  const [refazendo, setRefazendo] = useState<IntegrationCall | null>(null);

  const bloqueada = filtroMandatorio && !recorteSuficiente(aplicados);

  const bureaus = useQuery({
    queryKey: ['bureaus'],
    queryFn: ({ signal }) => integrationCalls.bureaus(signal),
  });
  const tipos = useQuery({
    queryKey: ['integration-call-types'],
    queryFn: ({ signal }) => integrationCalls.types(signal),
  });

  const parametros: IntegrationCallFilter = {
    kind: 'bureau',
    limit: LIMITE,
    offset,
    from: aplicados.from,
    to: aplicados.to,
    document: digitos(aplicados.document),
    provider_key: aplicados.provider_key,
  };
  if (aplicados.status) parametros.status = aplicados.status as IntegrationCallStatus;

  const consulta = useQuery({
    queryKey: ['integration-calls', 'bureau', parametros],
    queryFn: ({ signal }) => integrationCalls.list(parametros, signal),
    enabled: !bloqueada,
    placeholderData: keepPreviousData,
  });

  const muda = (campo: keyof Filtros) => (valor: string) =>
    setRascunho((atual) => ({ ...atual, [campo]: valor }));

  const aplicar = () => {
    setAplicados(rascunho);
    setOffset(0);
  };

  const colunas: Column<IntegrationCall>[] = [
    {
      key: 'data',
      header: 'Data/Hora',
      render: (call) => formatDateTime(call.started_at),
    },
    {
      key: 'id',
      header: 'Id da consulta',
      render: (call) => (
        <span className="font-mono text-2xs text-ink-700">
          {call.external_reference || 'Sem id do fornecedor'}
        </span>
      ),
    },
    {
      key: 'cnpj',
      header: 'CNPJ',
      render: (call) => (
        <div className="flex flex-col">
          <span className="whitespace-nowrap">{call.document}</span>
          {call.subject?.type === 'tomador' ? (
            <Link
              to={`/tomadores/${call.subject.id}`}
              className="text-2xs text-brand-600 underline-offset-2 hover:underline"
            >
              {call.subject.label || 'Ver tomador'}
            </Link>
          ) : null}
        </div>
      ),
    },
    {
      key: 'bureau',
      header: 'Bureau usado',
      render: (call) => nomeDoProvedor(call.provider_key, call.provider_name),
    },
    {
      key: 'situacao',
      header: 'Situacao',
      hideBelow: 'md',
      render: (call) => <CallStatusBadge call={call} />,
    },
    {
      key: 'mensagem',
      header: 'Mensagem',
      render: (call) => <span className="text-ink-700">{call.message}</span>,
    },
    {
      key: 'acoes',
      header: '',
      align: 'right',
      render: (call) => (
        <span className="inline-flex gap-1">
          <AcaoComIcone
            acao="visualizar"
            rotulo="Ver detalhes"
            onClick={() => setDetalhe(call.id)}
          />
          {podeReenviar && call.can_resend ? (
            <AcaoComIcone
              acao="refazer"
              rotulo="Refazer consulta"
              onClick={() => setRefazendo(call)}
            />
          ) : null}
        </span>
      ),
    },
  ];

  const pagina = consulta.data;
  const filtrando = Object.values(aplicados).some(Boolean);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Consultas a bureau"
        description="Consultas que a empresa fez aos bureaus de credito e cadastro, com o resultado e o custo."
      />

      {bloqueada ? (
        <Alert tone="warning" title="Atencao!">
          O seu perfil exige filtro antes de listar as consultas. Informe o periodo
          (data inicial e final) ou o CNPJ e use Aplicar.
        </Alert>
      ) : null}

      <Card>
        <CardBody className="flex flex-col gap-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
            Filtros
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Field
              label="Data inicial"
              type="date"
              value={rascunho.from}
              onChange={(event) => muda('from')(event.target.value)}
            />
            <Field
              label="Data final"
              type="date"
              value={rascunho.to}
              onChange={(event) => muda('to')(event.target.value)}
            />
            <Field
              label="CNPJ consultado"
              inputMode="numeric"
              value={rascunho.document}
              onChange={(event) => muda('document')(event.target.value)}
            />
            <Select
              label="Bureau"
              placeholder="Todos"
              options={(bureaus.data ?? []).map((bureau) => ({
                value: bureau.key,
                label: nomeDoProvedor(bureau.key, bureau.name),
              }))}
              value={rascunho.provider_key}
              onChange={(event) => muda('provider_key')(event.target.value)}
            />
            <Select
              label="Situacao da consulta"
              placeholder="Todas"
              options={opcoesDaSituacao}
              value={rascunho.status}
              onChange={(event) => muda('status')(event.target.value)}
            />
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setRascunho(filtrosVazios);
                setAplicados(filtrosVazios);
                setOffset(0);
              }}
            >
              Limpar filtro
            </Button>
            <Button onClick={aplicar}>Aplicar</Button>
          </div>
        </CardBody>
      </Card>

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar as consultas">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        {bloqueada ? (
          <EmptyState
            icon="alert"
            title="Informe o filtro para listar"
            description="Com o filtro mandatorio, a lista so aparece depois do periodo ou do CNPJ."
          />
        ) : (
          <>
            <DataTable
              columns={colunas}
              items={pagina?.items ?? []}
              keyOf={(call) => call.id}
              loading={consulta.isPending}
              empty={
                <EmptyState
                  icon="target"
                  title="Nenhuma consulta realizada ainda"
                  description={
                    filtrando
                      ? 'Nenhuma consulta atende aos filtros.'
                      : 'As consultas ao bureau feitas na ficha do tomador aparecem aqui.'
                  }
                />
              }
            />
            {pagina ? (
              <Pagination
                total={pagina.total}
                limit={pagina.limit}
                offset={pagina.offset}
                onChange={setOffset}
              />
            ) : null}
          </>
        )}
      </Card>

      {avisoAberto && bloqueada ? (
        <Modal
          open
          onClose={() => setAvisoAberto(false)}
          title="Atencao!"
          size="sm"
          footer={<Button onClick={() => setAvisoAberto(false)}>Entendi</Button>}
        >
          <p className="text-sm text-ink-600">
            Para listar as consultas a bureau, informe o periodo (data inicial e final)
            ou o CNPJ consultado nos filtros e use Aplicar.
          </p>
        </Modal>
      ) : null}

      {detalhe ? (
        <CallDetailModal callId={detalhe} onClose={() => setDetalhe(null)} />
      ) : null}

      {refazendo ? (
        <ResendDialog
          call={refazendo}
          costConfirmation={
            tipos.data?.find((tipo) => tipo.key === refazendo.call_type)
              ?.cost_confirmation ?? true
          }
          title="Refazer consulta"
          onClose={() => setRefazendo(null)}
        />
      ) : null}
    </div>
  );
}
